import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Phone, 
  Zap, 
  Clock, 
  MapPin, 
  CheckCircle,
  AlertTriangle,
  Smartphone,
  Laptop,
  Navigation
} from "lucide-react";
import { SEO } from "@/components/SEO";

const BUSINESS_PHONE = "05 49 05 74 90";
const BUSINESS_PHONE_LINK = "tel:+33549057490";
const GOOGLE_MAPS_URL = "https://www.google.com/maps/dir//104+Rue+de+la+Gare,+79000+Niort,+France";
const WHATSAPP_URL = "https://wa.me/33549057490?text=URGENCE%20:%20J'ai%20besoin%20d'une%20réparation%20urgente%20!";

const emergencyServices = [
  {
    icon: Smartphone,
    title: "Téléphone cassé",
    description: "Écran, batterie, connecteur",
    time: "30 min - 1h"
  },
  {
    icon: Laptop,
    title: "Ordinateur en panne",
    description: "Diagnostic et réparation",
    time: "1h - 2h"
  }
];

const guarantees = [
  "Intervention même jour (si avant 16h)",
  "Pièces en stock pour modèles courants",
  "Tarif transparent, sans surprise",
  "Garantie 6 mois maintenue"
];

export default function EmergencyPage() {
  useEffect(() => {}, []);

  return (
    <div className="min-h-screen emergency-bg" data-testid="emergency-page">
      <SEO
        title="Réparation Urgente Téléphone & Ordinateur à Niort - Intervention Express"
        description="Réparation urgente iPhone, Samsung, ordinateur Mac/PC à Niort. Intervention express jour même si avant 17h. Diagnostic immédiat. Techniciens certifiés Apple. ☎ 05 49 05 74 90"
        keywords="réparation urgente Niort, urgence téléphone Niort, urgence iPhone Niort, urgence ordinateur Niort, réparation express Niort, intervention rapide informatique Niort, réparation jour même Niort"
        path="/urgence"
        breadcrumbs={[
          { name: "Accueil", path: "/" },
          { name: "Urgence Réparation", path: "/urgence" },
        ]}
      />
      {/* Hero Section - Simplified for urgency */}
      <section className="py-12 md:py-20">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            {/* Urgency Badge */}
            <Badge className="badge-urgent px-6 py-3 text-lg mb-8 animate-pulse-slow">
              <AlertTriangle className="w-5 h-5 mr-2" />
              Service d&apos;urgence disponible
            </Badge>

            <h1 className="text-4xl md:text-6xl font-bold text-slate-900 mb-6">
              Réparation <span className="text-orange-500">Urgente</span>
            </h1>

            <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
              Votre appareil est en panne et vous en avez besoin rapidement ? 
              <strong className="text-slate-900"> Intervention express</strong>, même jour selon disponibilité.
            </p>

            {/* Main CTA - Phone */}
            <div className="flex flex-col items-center gap-6 mb-12">
              <a href={BUSINESS_PHONE_LINK}>
                <Button 
                  size="lg" 
                  className="bg-orange-500 hover:bg-orange-600 h-16 px-12 text-xl font-bold gap-3 animate-pulse-slow"
                  data-testid="emergency-call-btn"
                >
                  <Phone className="h-7 w-7" />
                  {BUSINESS_PHONE}
                </Button>
              </a>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                  <Button 
                    size="lg" 
                    variant="outline"
                    className="border-green-500 text-green-600 hover:bg-green-50 h-12 gap-2"
                    data-testid="emergency-whatsapp-btn"
                  >
                    <Zap className="h-5 w-5" />
                    WhatsApp Urgence
                  </Button>
                </a>
                <a href={GOOGLE_MAPS_URL} target="_blank" rel="noopener noreferrer">
                  <Button 
                    size="lg" 
                    variant="outline"
                    className="border-blue-500 text-blue-600 hover:bg-blue-50 h-12 gap-2"
                    data-testid="emergency-map-btn"
                  >
                    <Navigation className="h-5 w-5" />
                    Venir en boutique
                  </Button>
                </a>
              </div>
            </div>

            {/* Quick Info */}
            <div className="grid md:grid-cols-3 gap-4 max-w-3xl mx-auto">
              <Card className="bg-white/80 backdrop-blur">
                <CardContent className="p-4 flex items-center gap-3">
                  <Clock className="h-8 w-8 text-orange-500" />
                  <div className="text-left">
                    <p className="font-semibold text-slate-900">Intervention express</p>
                    <p className="text-sm text-slate-500">Même jour si avant 17h</p>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-white/80 backdrop-blur">
                <CardContent className="p-4 flex items-center gap-3">
                  <MapPin className="h-8 w-8 text-orange-500" />
                  <div className="text-left">
                    <p className="font-semibold text-slate-900">104 Rue de la Gare</p>
                    <p className="text-sm text-slate-500">79000 Niort</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white/80 backdrop-blur">
                <CardContent className="p-4 flex items-center gap-3">
                  <CheckCircle className="h-8 w-8 text-green-500" />
                  <div className="text-left">
                    <p className="font-semibold text-slate-900">Ouvert maintenant</p>
                    <p className="text-sm text-slate-500">Lun-Ven 10h-18h</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Emergency Services */}
      <section className="py-12 bg-white">
        <div className="container-custom">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-slate-900 mb-8">
            Réparations express disponibles
          </h2>

          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {emergencyServices.map((service) => {
              const Icon = service.icon;
              return (
                <Card key={service.title} className="border-2 border-orange-200 hover:border-orange-400 transition-colors">
                  <CardContent className="p-6 flex items-center gap-4">
                    <div className="w-14 h-14 rounded-xl bg-orange-100 flex items-center justify-center flex-shrink-0">
                      <Icon className="h-7 w-7 text-orange-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-slate-900">{service.title}</h3>
                      <p className="text-slate-600">{service.description}</p>
                      <p className="text-sm text-orange-600 font-medium mt-1">
                        <Clock className="h-4 w-4 inline mr-1" />
                        {service.time}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Guarantees */}
      <section className="py-12 bg-slate-900 text-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold mb-8">
              Nos garanties urgence
            </h2>
            
            <div className="grid md:grid-cols-2 gap-4">
              {guarantees.map((guarantee) => (
                <div key={guarantee} className="flex items-center gap-3 bg-white/10 rounded-lg p-4">
                  <CheckCircle className="h-6 w-6 text-green-400 flex-shrink-0" />
                  <span className="text-left">{guarantee}</span>
                </div>
              ))}
            </div>

            <div className="mt-10">
              <a href={BUSINESS_PHONE_LINK}>
                <Button 
                  size="lg" 
                  className="bg-orange-500 hover:bg-orange-600 h-14 px-10 text-lg font-bold gap-2"
                  data-testid="emergency-bottom-call-btn"
                >
                  <Phone className="h-6 w-6" />
                  Appeler maintenant
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-12">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-center text-slate-900 mb-6">
              Nous trouver
            </h2>
            <div className="rounded-xl overflow-hidden shadow-lg h-[300px]">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2775.5!2d-0.4640!3d46.3230!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4806476e21ecbfed%3A0x4a1fa3c7e8d0f8a!2s104%20Rue%20de%20la%20Gare%2C%2079000%20Niort%2C%20France!5e0!3m2!1sfr!2sfr!4v1"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                title="Localisation Mediastore"
              />
            </div>
            <div className="mt-4 text-center">
              <a href={GOOGLE_MAPS_URL} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="gap-2">
                  <Navigation className="h-4 w-4" />
                  Ouvrir dans Google Maps
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
